import React, { useContext } from 'react';
import { Box, Card, CardContent, CardHeader, Typography, Button, Alert } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { AppContext } from 'views/App/context';
import useTrainingCourses from '../_data_sources/useTrainingCourses';
import Loading from 'views/App/Portal/Loading';
import { getLmsUrl } from 'data_sources/constants';

export default function RecommendedContent() {
  const { currentOrganizationId } = useContext(AppContext);

  const { data: coursesData, isLoading } = useTrainingCourses(currentOrganizationId, {
    limit: '10',
  });

  const recommendedContent = coursesData?.content || [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader title={t('dashboard.training.recommendedContent.title')} />
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <Loading />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!recommendedContent || recommendedContent.length === 0) {
    return (
      <Card>
        <CardHeader title={t('dashboard.training.recommendedContent.title')} />
        <CardContent>
          <Alert severity='info'>{t('dashboard.training.recommendedContent.empty')}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={t('dashboard.training.recommendedContent.title')} />
      <CardContent>
        <Box
          sx={{
            position: 'relative',
            '& .swiper': {
              paddingBottom: 1,
            },
            '& .swiper-button-next, & .swiper-button-prev': {
              color: neutral[400],
              '&:hover': {
                color: neutral[600],
              },
              '&:disabled': {
                opacity: 0.3,
                cursor: 'not-allowed',
              },
            },
            '& .swiper-button-next::after, & .swiper-button-prev::after': {
              fontSize: '18px',
            },
            '& .swiper-button-disabled': {
              opacity: 0.3,
            },
          }}
        >
          <Swiper
            modules={[Navigation]}
            spaceBetween={16}
            slidesPerView={1}
            breakpoints={{
              1200: { slidesPerView: 2 },
              1536: { slidesPerView: 3 },
            }}
            navigation
          >
            {recommendedContent.map((item) => (
              <SwiperSlide key={item.id}>
                <Box
                  sx={{
                    height: 180,
                  }}
                >
                  <Box
                    sx={{
                      border: `solid 1px ${neutral[200]}`,
                      borderRadius: 2,
                      backgroundColor: neutral[25],
                      display: 'grid',
                      gridTemplateColumns: '180px 1fr',
                      height: '100%',
                    }}
                  >
                    {item.thumbnail?.medium && (
                      <Box
                        component='img'
                        src={
                          item.thumbnail.medium.startsWith('//')
                            ? `https:${item.thumbnail.medium}`
                            : item.thumbnail.medium
                        }
                        alt={item.name}
                        sx={{
                          width: 180,
                          height: 180,
                          objectFit: 'cover',
                          borderRadius: '8px 0 0 8px',
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: 1,
                        p: 4,
                      }}
                    >
                      <Typography
                        variant='eyebrowMd'
                        sx={{ color: neutral[500] }}
                      >
                        {t('dashboard.training.recommendedContent.course')}
                      </Typography>
                      <Typography variant='subtitleMd'>{item.name}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          variant='text'
                          size='small'
                          href={getLmsUrl(
                            `/assigned-learning/assignments/create?course=${encodeURIComponent(item.slug)}`,
                          )}
                        >
                          {t('dashboard.training.recommendedContent.assign')}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </CardContent>
    </Card>
  );
}
